// src/hooks/dutchie-queries.js
import { useQuery as useApolloQuery, useApolloClient } from '@apollo/client';
import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { consoleDebug, getPriceFilteredProducts } from '../util';
import * as storage from '../browserStorage';
import { storageItemName } from '../config';
import { FETCH_POLICIES, useApolloMenuProducts } from '../utils/apolloQueries';

// Import your GraphQL documents
import MENU_QUERY from '../api/queries/MenuQuery.gql';
import GET_SPECIALS_LIST from '../api/queries/GetSpecialsList.gql';
import RETAILER_QUERY from '../api/queries/RetailerQuery.gql';
import PRODUCT_QUERY from '../api/queries/ProductQuery.gql';
import ORDERS_BY_ORDER_NUMBER from '../api/queries/OrdersByOrderNumber.gql';

export const useNewMenuProducts = (retailerId, queryArgsFromComponent, options = {}) => {
    const { apiPageSize = 20, enabled = true } = options;
    
    // Check if it's a specials query
    const isSpecialsQuery = queryArgsFromComponent?.queryName === 'MenuBySpecialsSection';
    
    // Check if it's a single special (not an array of specials)
    const isSingleSpecialQuery = isSpecialsQuery && 
        queryArgsFromComponent?.variables?.menuFilter?.menuSection?.specialId &&
        !Array.isArray(queryArgsFromComponent.variables.menuFilter.menuSection.specialId);
    
    // Use 500 limit only for single special queries
    const initialFetchLimit = isSingleSpecialQuery ? 500 : apiPageSize;

    const variables = useMemo(() => ({
        ...(queryArgsFromComponent?.variables || {}),
        retailerId,
        offset: 0,
        limit: initialFetchLimit,
    }), [queryArgsFromComponent?.variables, retailerId, initialFetchLimit]);

    const { data, loading, error, fetchMore, refetch, networkStatus } = useApolloMenuProducts(
        MENU_QUERY,
        variables,
        {
            skip: !enabled || !queryArgsFromComponent,
            fetchPolicy: isSpecialsQuery ? FETCH_POLICIES.SPECIALS : FETCH_POLICIES.MENU,
        }
    );

    const menuData = data?.menu;
    const products = menuData?.products || [];
    const totalProductsCount = isSingleSpecialQuery ? products.length : menuData?.productsCount;

    const hasNextPage = useMemo(() => {
        if (isSingleSpecialQuery) return false; // No pagination for single specials
        if (totalProductsCount === undefined || products.length === 0) return false;
        return products.length < totalProductsCount;
    }, [products.length, totalProductsCount, isSingleSpecialQuery]);

    const fetchNextPage = useCallback(() => {
        if (isSingleSpecialQuery || !hasNextPage || !fetchMore || loading) {
            return Promise.resolve();
        }
        
        return fetchMore({
            variables: {
                offset: products.length,
                limit: apiPageSize,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                
                return {
                    ...fetchMoreResult,
                    menu: {
                        ...fetchMoreResult.menu,
                        products: [...prev.menu.products, ...fetchMoreResult.menu.products]
                    }
                };
            }
        });
    }, [fetchMore, products.length, apiPageSize, hasNextPage, loading, isSingleSpecialQuery]);

    // isLoading should be true for any loading state except pagination (networkStatus 3)
    // networkStatus: 1=loading, 2=setVariables, 3=fetchMore, 4=refetch, 7=ready
    const isLoading = loading && networkStatus !== 3;
    const isFetchingNextPage = networkStatus === 3;

    return {
        allProducts: products,
        totalProductsCount,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        error,
        refetch,
    };
};

export const useProductsGridMenu = (
    retailerId,
    queryVars,
    priceFilters,
    pricingType,
    productAtts,
    isRelatedProducts,
    paginationLimit
) => {
    // Create stable variables to prevent infinite loops
    const stableQueryVars = useMemo(() => {
        if (!queryVars) return null;
        // Deep clone to ensure stability
        return JSON.parse(JSON.stringify(queryVars));
    }, [JSON.stringify(queryVars)]); // Stringify to check deep equality
    
    const skip = !Boolean(stableQueryVars !== null && !productAtts && !isRelatedProducts);
    
    const { data, loading, error, refetch, networkStatus } = useApolloQuery(MENU_QUERY, {
        variables: stableQueryVars,
        skip,
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all',
        onError: (error) => {
            console.error('Products grid query error:', error);
        }
    });

    // Process products with price filter if needed
    const processedProducts = useMemo(() => {
        const products = data?.menu?.products;
        if (!products) return [];
        
        if (priceFilters !== null) {
            return getPriceFilteredProducts(
                products,
                priceFilters,
                pricingType,
                paginationLimit
            );
        }
        return products;
    }, [data?.menu?.products, priceFilters, pricingType, paginationLimit]);

    // Log warnings for non-fatal errors
    useMemo(() => {
        if (error && data?.menu?.products) {
            console.warn('Non-fatal menu query errors:', error);
        }
    }, [error, data?.menu?.products]);

    // Debug logging
    consoleDebug('useProductsGridMenu:', {
        skip,
        loading,
        networkStatus,
        hasData: !!data,
        productsCount: processedProducts.length,
        queryVars: stableQueryVars
    });

    // Return format compatible with existing code
    return {
        data: processedProducts,
        isLoading: loading && !data, // Only loading on first fetch
        isError: error && !data?.menu?.products,
        error: error && !data?.menu?.products ? error : null,
        isFetched: !loading || networkStatus === 7,
        refetch
    };
};

export const useRelatedProducts = (
    retailerId,
    filter,
    paginationLimit,
    isRelatedProducts,
    getVariables,
    excludeProductId = null
) => {
    const client = useApolloClient();
    const [state, setState] = useState({
        products: [],
        loading: false,
        error: null,
        isFetched: false
    });

    // Incremented per fetch so a superseded request (e.g. the popup switching to
    // another product before the first fetch resolves) can't overwrite newer results.
    const requestIdRef = useRef(0);

    // Create stable variables for the initial query
    const initialVariables = useMemo(() => {
        if (!isRelatedProducts || !filter) return null;
        return getVariables(filter);
    }, [isRelatedProducts, filter, getVariables]);

    // Manual query execution function
    const fetchRelatedProducts = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        const isCurrentRequest = () => requestId === requestIdRef.current;

        if (!isRelatedProducts || !initialVariables) {
            setState({
                products: [],
                loading: false,
                error: null,
                isFetched: true
            });
            return;
        }

        setState(prev => ({
            ...prev,
            loading: true,
            error: null,
            isFetched: false
        }));

        // Ask for one extra product so excluding the current product doesn't
        // leave the slider one short.
        const fetchLimit = paginationLimit + (excludeProductId ? 1 : 0);

        const queryProducts = async (menuFilter) => {
            const vars = getVariables(menuFilter);

            // Match the pattern from useNewMenuProducts more closely
            const { data, error: queryError } = await client.query({
                query: MENU_QUERY,
                variables: {
                    ...vars,
                    limit: fetchLimit,
                    retailerId, // Ensure retailerId is included
                },
                fetchPolicy: 'network-only', // Match useNewMenuProducts behavior for fresh data
                errorPolicy: 'all',
            });

            // Check for errors but continue if we have partial data (matching the pattern)
            if (queryError) {
                console.warn('⚠️ Related products query warning:', queryError);
                if (!data?.menu?.products) {
                    throw new Error('No products available with current filters.');
                }
            }

            return data?.menu?.products || [];
        };

        try {
            // Never show the product being viewed, and never show the same
            // product twice when the broadened query overlaps the first one.
            const seenIds = new Set(excludeProductId ? [excludeProductId] : []);
            const relatedProducts = [];

            const collect = (products) => {
                products.forEach((product) => {
                    if (!product?.id || seenIds.has(product.id)) return;
                    seenIds.add(product.id);
                    relatedProducts.push(product);
                });
            };

            // Pass 1: same category, brand and strain type.
            collect(await queryProducts(filter));

            // Pass 2: same category only. Skipped when pass 1 was already that
            // broad, since repeating an identical query can only add duplicates.
            const hadNarrowFilter = Boolean(filter.strainType) || (filter.brandIds?.length > 0);

            if (relatedProducts.length < paginationLimit && hadNarrowFilter) {
                collect(await queryProducts({
                    ...filter,
                    strainType: '',
                    brandIds: [],
                }));
            }

            if (!isCurrentRequest()) return;

            setState({
                products: relatedProducts.slice(0, paginationLimit),
                loading: false,
                error: null,
                isFetched: true
            });
        } catch (err) {
            if (!isCurrentRequest()) return;

            console.error('❌ Related products fetch error:', err);
            setState({
                products: [],
                loading: false,
                error: err,
                isFetched: true
            });
        }
    }, [client, filter, getVariables, initialVariables, isRelatedProducts, paginationLimit, retailerId, excludeProductId]);

    // Execute query when dependencies change
    useEffect(() => {
        fetchRelatedProducts();
    }, [fetchRelatedProducts]);

    // Refetch function
    const refetch = useCallback(() => {
        return fetchRelatedProducts();
    }, [fetchRelatedProducts]);

    // Return format compatible with the old hook
    return {
        data: state.products,
        isLoading: state.loading,
        isError: !!state.error,
        error: state.error,
        isFetched: state.isFetched,
        refetch
    };
};

export const useRetailerData = (retailerId) => {
    const { data, loading, error, refetch } = useApolloQuery(
        RETAILER_QUERY,
        {
            variables: { retailerId },
            skip: !retailerId,
            ...FETCH_POLICIES.RETAILER,
        }
    );
    
    return {
        data: data?.retailer,
        isFetched: !loading && !error,
        loading,
        error,
        refetch
    };
};

export const useSingleProducts = (retailerId, productAtts) => {
    const client = useApolloClient();
    const [results, setResults] = useState([]);
    
    // Create a stable array of product IDs to avoid hook order issues
    const stableProductIds = useMemo(() => 
        productAtts || [], 
        [JSON.stringify(productAtts)]
    );
    
    // Execute queries
    useEffect(() => {
        if (!stableProductIds.length || !retailerId) {
            setResults([]);
            return;
        }
        
        let cancelled = false;
        const fetchAllProducts = async () => {
            const promises = stableProductIds.map(productId => 
                client.query({
                    query: PRODUCT_QUERY,
                    variables: { retailerId, productId },
                    fetchPolicy: 'cache-first', // Use cache since products are normalized
                    errorPolicy: 'all'
                })
            );
            
            try {
                const responses = await Promise.allSettled(promises);
                
                if (!cancelled) {
                    const processedResults = responses.map((response, index) => {
                        if (response.status === 'fulfilled') {
                            const { data, error } = response.value;
                            
                            if (error && !data?.product) {
                                return { 
                                    data: null, 
                                    error, 
                                    loading: false,
                                    refetch: () => promises[index]
                                };
                            }
                            
                            return { 
                                data: data?.product || null, 
                                error: null, 
                                loading: false,
                                refetch: () => promises[index]
                            };
                        } else {
                            return { 
                                data: null, 
                                error: response.reason, 
                                loading: false,
                                refetch: () => promises[index]
                            };
                        }
                    });
                    
                    setResults(processedResults);
                }
            } catch (error) {
                if (!cancelled) {
                    setResults([]);
                }
            }
        };
        
        fetchAllProducts();
        
        return () => {
            cancelled = true;
        };
    }, [client, retailerId, stableProductIds]);
    
    // Match the original return format
    const productsFetched = results.length > 0 && results.some(r => r.data !== null);
    
    return {
        productsFetched,
        productsData: productsFetched ? results.map(r => r.data) : null,
        productsRefetchFns: results.map(r => r.refetch),
        // Additional properties for debugging
        loading: results.some(r => r.loading),
        errors: results.filter(r => r.error).map(r => r.error)
    };
};

const specialsCache = new Map();
const SPECIALS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useSpecialsList = (retailerId) => {
    // Check cache first
    const cacheKey = `specials_${retailerId}`;
    const cached = specialsCache.get(cacheKey);
    const now = Date.now();
    
    // Use cached data if available and not expired
    const shouldSkipQuery = cached && 
                           cached.timestamp && 
                           (now - cached.timestamp) < SPECIALS_CACHE_DURATION;
    
    const { data, loading, error, refetch } = useApolloQuery(
        GET_SPECIALS_LIST,
        {
            variables: { retailerId },
            skip: !retailerId,
            ...FETCH_POLICIES.SPECIALS,
            pollInterval: 300000, // Poll every 5 minutes for fresh specials
            notifyOnNetworkStatusChange: true,
        }
    );

    // DEBUG: Log specials query state
    useEffect(() => {
        consoleDebug('📋 SPECIALS QUERY DEBUG: useSpecialsList state changed', {
            timestamp: new Date().toISOString(),
            retailerId,
            hasRetailerId: !!retailerId,
            loading,
            hasError: !!error,
            errorMessage: error?.message,
            hasData: !!data,
            specialsCount: data?.specials?.length || 0,
            shouldSkipQuery,
            cacheKey,
            hasCachedData: !!cached
        });
    }, [loading, error, data, retailerId, shouldSkipQuery, cacheKey, cached]);

    // Use cached data if we skipped the query
    const effectiveData = shouldSkipQuery ? cached.data : data;

    const filteredSpecials = useMemo(() => {
        // Ensure data is an array
        if (!effectiveData?.specials || !Array.isArray(effectiveData.specials)) {
            return [];
        }

        // Only log once per unique retailer
        const logKey = `logged_${retailerId}`;
        const hasLogged = specialsCache.has(logKey);

        const filtered = effectiveData.specials.filter((special) => {
            if (!special) return false;

            if (special.hasOwnProperty('scheduleConfiguration')) {
                const {
                    days,
                    endDate,
                    endStamp,
                    recurringEndTime,
                    recurringStartTime,
                    setEndDate,
                    startStamp,
                } = special.scheduleConfiguration || {};

                // Get the current browser time
                const current = new Date();
                const currentISO = current.toISOString();

                // Get current day in local timezone (0 = Sunday, 1 = Monday, etc.)
                const currentDay = current.getDay();

                // Convert current time to hours and minutes for comparison
                const currentHours = current.getHours();
                const currentMinutes = current.getMinutes();
                const currentTimeInMinutes = currentHours * 60 + currentMinutes;

                // Parse recurring times to compare with current time
                let startTimeInMinutes = null;
                let endTimeInMinutes = null;

                if (recurringStartTime && recurringEndTime) {
                    // Parse times like "04:00 AM" or "11:00 AM"
                    const parseTimeString = (timeStr) => {
                        if (!timeStr) return null;

                        try {
                            const [timePart, ampm] = timeStr.split(' ');
                            let [hours, minutes] = timePart.split(':').map(Number);

                            // Convert 12-hour format to 24-hour
                            if (ampm && ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
                            if (ampm && ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;

                            return hours * 60 + minutes; // Convert to minutes since midnight
                        } catch (e) {
                            return null;
                        }
                    };

                    startTimeInMinutes = parseTimeString(recurringStartTime);
                    endTimeInMinutes = parseTimeString(recurringEndTime);
                }

                // Debug log to see the time calculations - only log once per retailer
                if (!hasLogged && special.id === effectiveData.specials[0]?.id) {
                    consoleDebug('🔍 SPECIAL FILTERING:', {
                        retailerId: retailerId,
                        id: special.id,
                        name: special.name,
                        days: days,
                        currentDay: currentDay,
                        recurringStartTime,
                        recurringEndTime,
                        startTimeInMinutes,
                        endTimeInMinutes,
                        currentTimeInMinutes,
                        currentTime: `${currentHours}:${currentMinutes.toString().padStart(2, '0')}`,
                        startStamp,
                        endStamp,
                        currentISO,
                        isActive: startStamp && startStamp < currentISO,
                        isWithinTimeWindow: startTimeInMinutes === null || endTimeInMinutes === null ||
                            (currentTimeInMinutes >= startTimeInMinutes &&
                                currentTimeInMinutes <= endTimeInMinutes)
                    });
                    // Mark as logged
                    specialsCache.set(logKey, true);
                }

                // Check if the special has started based on startStamp
                if (startStamp && startStamp < currentISO) {
                    // Check if the special has ended
                    if ((setEndDate || endDate) && (endDate < currentISO || endStamp < currentISO)) {
                        return false;
                    }

                    // Check if specific days are configured and today is included
                    if (days && Array.isArray(days)) {
                        if (!days.includes(currentDay)) {
                            return false;
                        }
                    }

                    // Check time restrictions if both start and end times are provided
                    if (startTimeInMinutes !== null && endTimeInMinutes !== null) {
                        let isWithinWindow;

                        // Handle cases where time range spans midnight
                        if (startTimeInMinutes > endTimeInMinutes) {
                            // Example: 10:00 PM to 4:00 AM
                            isWithinWindow =
                                currentTimeInMinutes >= startTimeInMinutes ||
                                currentTimeInMinutes <= endTimeInMinutes;
                        } else {
                            // Normal case: 9:00 AM to 5:00 PM
                            isWithinWindow =
                                currentTimeInMinutes >= startTimeInMinutes &&
                                currentTimeInMinutes <= endTimeInMinutes;
                        }

                        if (!isWithinWindow) {
                            return false;
                        }
                    }

                    // If we've passed all checks, the special is active
                    return true;
                }

                return false;
            }

            return false;
        });

        if (!hasLogged && effectiveData.specials.length > 0) {
            consoleDebug(`Filtered ${effectiveData.specials.length} specials down to ${filtered.length} active specials`);
        }
        
        return filtered || [];
    }, [effectiveData?.specials, retailerId]);

    // Update cache when we get new data
    useEffect(() => {
        if (data?.specials && !shouldSkipQuery) {
            specialsCache.set(cacheKey, {
                data: data,
                timestamp: now
            });
        }
    }, [data, cacheKey, now, shouldSkipQuery]);

    // Clear cache on unmount if it's the last component using this retailer
    useEffect(() => {
        return () => {
            // Clean up old cache entries on unmount
            const cutoffTime = now - SPECIALS_CACHE_DURATION;
            for (const [key, value] of specialsCache.entries()) {
                if (value.timestamp && value.timestamp < cutoffTime) {
                    specialsCache.delete(key);
                }
            }
        };
    }, [now]);

    // Calculate isFetched
    const isFetchedValue = shouldSkipQuery ? true : !loading;

    // DEBUG: Log return values
    useEffect(() => {
        consoleDebug('📋 SPECIALS QUERY DEBUG: useSpecialsList returning', {
            timestamp: new Date().toISOString(),
            retailerId,
            isFetched: isFetchedValue,
            isLoading: shouldSkipQuery ? false : loading,
            isError: shouldSkipQuery ? false : !!error,
            filteredSpecialsCount: filteredSpecials.length,
            calculation: {
                shouldSkipQuery,
                loading,
                computed_isFetched: isFetchedValue
            }
        });
    }, [isFetchedValue, loading, filteredSpecials.length, retailerId, shouldSkipQuery, error]);

    // Return format compatible with existing code
    return {
        data: filteredSpecials,
        loading: shouldSkipQuery ? false : loading,
        error: shouldSkipQuery ? null : error,
        refetch,
        // Additional properties for compatibility with TanStack Query interface
        isLoading: shouldSkipQuery ? false : loading,
        isError: shouldSkipQuery ? false : !!error,
        // isFetched should be true when query completes, regardless of error state
        // This matches React Query behavior where isFetched = query has completed (success or error)
        isFetched: isFetchedValue,
        // For debugging
        rawSpecials: effectiveData?.specials,
        totalSpecialsCount: effectiveData?.specials?.length || 0,
        activeSpecialsCount: filteredSpecials.length
    };
};

export const useSingleProduct = (retailerId, productId, { enabled }) => {
    const { data, loading, error } = useApolloQuery(
        PRODUCT_QUERY,
        {
            variables: { retailerId, productId },
            skip: !enabled || !retailerId || !productId,
            ...FETCH_POLICIES.PRODUCT,
        }
    );
    
    return {
        data: data?.product,
        loading,
        error
    };
};

export const useBatchProducts = (retailerId, productIds = []) => {
    const client = useApolloClient();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!productIds.length || !retailerId) return;

        const fetchProducts = async () => {
            setLoading(true);
            setError(null);

            try {
                const results = await Promise.all(
                    productIds.map(productId =>
                        client.query({
                            query: PRODUCT_QUERY,
                            variables: { retailerId, productId },
                            ...FETCH_POLICIES.PRODUCT
                        })
                    )
                );

                setProducts(results.map(result => result.data?.product).filter(Boolean));
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [retailerId, productIds.join(','), client]);

    return { products, loading, error };
};

export const useOrdersByOrderNumber = (
    retailerId,
    orderNumber,
    handleTracking = null,
    analytics = null
) => {
    const { data, loading, error } = useApolloQuery(
        ORDERS_BY_ORDER_NUMBER,
        {
            variables: { retailerId, orderNumber },
            skip: !retailerId || !orderNumber,
            fetchPolicy: 'network-only', // Always fetch fresh order data
            notifyOnNetworkStatusChange: true,
            onCompleted: (data) => {
                if (data?.orders?.length > 0) {
                    const order = data.orders[0];
                    
                    if (handleTracking) {
                        handleTracking(orderNumber, order, analytics);
                    }
                    
                    // Clear cart storage after successful order
                    storage.removeItem(storageItemName);
                    
                    consoleDebug('Order fetched successfully:', {
                        orderNumber,
                        order: order,
                        orderId: order.id,
                        total: order.total,
                        itemCount: order.items?.length
                    });
                }
            },
            onError: (error) => {
                console.error('Failed to fetch order:', error);
                // Still clear cart storage on error to prevent issues
                storage.removeItem(storageItemName);
            }
        }
    );

    return {
        data: data?.orders?.[0] || null,
        isLoading: loading,
        isFetched: !loading,
        error
    };
};
